import { ElementType, FC, ReactNode, useState } from 'react';
import Highlighter from 'react-highlighter';
import { useTranslation } from 'react-i18next';

import { CategoryCardSkeleton } from './CategoryCardSkeleton';

import {
  ArrowRightIcon,
  DeleteIcon,
  EditIcon,
  GlobeIcon,
  InfoIcon,
  MoreVerticalIcon,
} from '@/assets/icons';
import {
  Button,
  Card,
  CardContent,
  IconBox,
  IconButton,
  Separator,
  Tooltip,
} from '@/components/atoms';
import {
  Dropdown,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuSimpleItem,
  DropdownMenuTrigger,
} from '@/components/organisms';

type CategoryCardProps = {
  icon: ElementType;
  title: string;
  questionCount: number;
  questions: string[];
  helpText?: ReactNode;
  search?: string;
  isLoading?: boolean;
  onClick: () => void;
  onTranslationClick: () => void;
  onEditClick: () => void;
  onDeleteClick: () => void;
};

export const CategoryCard: FC<CategoryCardProps> = ({
  icon,
  title,
  questionCount,
  questions,
  helpText,
  search,
  isLoading,
  onClick,
  onDeleteClick,
  onEditClick,
  onTranslationClick,
}) => {
  const { t } = useTranslation();

  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  const hasQuestions = questions.length > 0;

  if (isLoading) {
    return <CategoryCardSkeleton />;
  }

  const handleTransaltionClick = () => {
    onTranslationClick();
    setIsOpenDropdown(false);
  };
  const handleEditClick = () => {
    onEditClick();
    setIsOpenDropdown(false);
  };
  const handleDeleteClick = () => {
    onDeleteClick();
    setIsOpenDropdown(false);
  };

  return (
    <Card className="rounded-[12px] p-4">
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-start">
          <div className="mr-3">
            <IconBox icon={icon} size="large" variant="default-cyan" />
          </div>
          <div className="mr-2 grow">
            <p className="text-body-14-semibold text-custom-warm-grey-800 mb-2">{title}</p>
            <p className="text-body-12-regular text-custom-warm-grey-500">
              {t('components:categoryCard.questionsCount', { questionCount })}
            </p>
          </div>
          <div className="flex items-center gap-1 text-custom-warm-grey-400">
            <Tooltip title={helpText}>
              <IconButton icon={<InfoIcon width={20} height={20} />} size="sm" variant="teritary" />
            </Tooltip>
            <Dropdown open={isOpenDropdown} onOpenChange={setIsOpenDropdown}>
              <DropdownMenuTrigger data-testid="category-card" variant="transparent" asChild>
                <IconButton
                  icon={<MoreVerticalIcon width={20} height={20} />}
                  size="sm"
                  variant="teritary"
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuSimpleItem onClick={handleTransaltionClick}>
                  <GlobeIcon width={20} height={20} className="text-custom-warm-grey-500" />
                  {t('components:categoryCard.dropdown.translationButton')}
                </DropdownMenuSimpleItem>
                <DropdownMenuSimpleItem onClick={handleEditClick}>
                  <EditIcon width={20} height={20} className="text-custom-warm-grey-500" />
                  {t('components:categoryCard.dropdown.editButton')}
                </DropdownMenuSimpleItem>
                <DropdownMenuSeparator />
                <DropdownMenuSimpleItem onClick={handleDeleteClick}>
                  <DeleteIcon width={20} height={20} className="text-custom-warm-grey-500" />
                  {t('components:categoryCard.dropdown.deleteButton')}
                </DropdownMenuSimpleItem>
              </DropdownMenuContent>
            </Dropdown>
          </div>
        </div>

        <div className="flex flex-col gap-2 min-h-[91px]">
          {hasQuestions ? (
            questions.map(question => (
              <div key={question}>
                <p className="text-body-12-regular text-custom-warm-grey-500">
                  <Highlighter search={search ?? ''}>{question}</Highlighter>
                </p>
                <Separator className="mt-2" />
              </div>
            ))
          ) : (
            <p className="text-body-12-regular text-custom-warm-grey-500">
              {t('components:categoryCard.noQuestions')}
            </p>
          )}
        </div>

        <div>
          <Button
            rightIcon={
              <ArrowRightIcon width={20} height={20} className="text-custom-warm-grey-500" />
            }
            size="xs"
            variant="teritary-bright"
            onClick={onClick}
          >
            {hasQuestions
              ? t('components:categoryCard.exploreQuestionsButton')
              : t('components:categoryCard.addQuestionsButton')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
